// Issue #359: Modal for creating/editing scheduled test data
import React, { useState, useEffect, useCallback } from 'react';
import { betaApi } from '../../services/api';

function CreateTestDataModal({ item, universities, onClose, onSuccess }) {
  const isEditing = !!item;

  const [formData, setFormData] = useState({
    content_type: 'post',
    university: '',
    scheduled_at: '',
    content: '',
    title: '',
    price: '',
    marketplace_type: 'offer',
    is_free_giveaway: false,
    is_anonymous: true,
    parent_post_id: '',
    course_id: '',
    rating: 5,
    image_urls: [],
  });

  const [posts, setPosts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data when editing
  useEffect(() => {
    if (item) {
      const scheduledAt = item.scheduled_at
        ? new Date(item.scheduled_at).toISOString().slice(0, 16)
        : '';

      let imageUrls = [];
      if (item.image_urls) {
        try {
          imageUrls = JSON.parse(item.image_urls);
        } catch (e) {
          imageUrls = [];
        }
      }

      setFormData({
        content_type: item.content_type || 'post',
        university: item.university || '',
        scheduled_at: scheduledAt,
        content: item.content || '',
        title: item.title || '',
        price: item.price || '',
        marketplace_type: item.marketplace_type || 'offer',
        is_free_giveaway: item.is_free_giveaway || false,
        is_anonymous: item.is_anonymous !== false, // Default to true
        parent_post_id: item.parent_post_id || '',
        course_id: item.course_id || '',
        rating: item.rating || 5,
        image_urls: imageUrls,
      });
    }
  }, [item]);

  // Load posts when university changes (for comments)
  const fetchPosts = useCallback(async (university) => {
    if (!university) {
      setPosts([]);
      return;
    }
    try {
      const response = await betaApi.getPosts(university, 50);
      setPosts(response.data || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
    }
  }, []);

  // Load courses when university changes (for course reviews)
  const fetchCourses = useCallback(async (university) => {
    if (!university) {
      setCourses([]);
      return;
    }
    try {
      const response = await betaApi.getCourses(university, 100);
      setCourses(response.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  }, []);

  useEffect(() => {
    if (formData.content_type === 'comment') {
      fetchPosts(formData.university);
    } else if (formData.content_type === 'course_review') {
      fetchCourses(formData.university);
    }
  }, [formData.university, formData.content_type, fetchPosts, fetchCourses]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare data for API
      const apiData = {
        content_type: formData.content_type,
        university: formData.university,
        scheduled_at: new Date(formData.scheduled_at).toISOString(),
        content: formData.content,
        is_anonymous: formData.is_anonymous,
      };

      // Add type-specific fields
      if (formData.content_type === 'marketplace') {
        apiData.title = formData.title;
        apiData.price = formData.price;
        apiData.marketplace_type = formData.marketplace_type;
        apiData.is_free_giveaway = formData.is_free_giveaway;
      }

      if (formData.content_type === 'comment') {
        apiData.parent_post_id = formData.parent_post_id;
      }

      if (formData.content_type === 'course_review') {
        apiData.course_id = formData.course_id;
        apiData.rating = parseInt(formData.rating, 10);
      }

      if (formData.image_urls && formData.image_urls.length > 0) {
        apiData.image_urls = formData.image_urls;
      }

      if (isEditing) {
        await betaApi.update(item.id, apiData);
      } else {
        await betaApi.create(apiData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving test data:', err);
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleAddImageUrl = () => {
    const url = prompt('Bild-URL eingeben:');
    if (url) {
      setFormData((prev) => ({
        ...prev,
        image_urls: [...prev.image_urls, url],
      }));
    }
  };

  const handleRemoveImageUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Testdaten bearbeiten' : 'Neue Testdaten erstellen'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Inhaltstyp *</label>
              <select
                name="content_type"
                value={formData.content_type}
                onChange={handleChange}
                required
                disabled={isEditing}
              >
                <option value="post">Beitrag</option>
                <option value="marketplace">Marktplatzobjekt</option>
                <option value="comment">Kommentar</option>
                <option value="course_review">Kursbewertung</option>
              </select>
            </div>

            <div className="form-group">
              <label>Universität *</label>
              <select
                name="university"
                value={formData.university}
                onChange={handleChange}
                required
              >
                <option value="">Bitte wählen</option>
                {universities.map((uni) => (
                  <option key={uni} value={uni}>{uni}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Geplante Uploadzeit *</label>
            <input
              type="datetime-local"
              name="scheduled_at"
              value={formData.scheduled_at}
              onChange={handleChange}
              required
            />
          </div>

          {/* Marketplace-specific fields */}
          {formData.content_type === 'marketplace' && (
            <>
              <div className="form-group">
                <label>Titel *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="z.B. iPhone 13 zu verkaufen"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Preis</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="z.B. 500 EUR"
                  />
                </div>

                <div className="form-group">
                  <label>Marktplatz-Typ *</label>
                  <select
                    name="marketplace_type"
                    value={formData.marketplace_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="offer">Angebot</option>
                    <option value="search">Suche</option>
                  </select>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  name="is_free_giveaway"
                  id="is_free_giveaway"
                  checked={formData.is_free_giveaway}
                  onChange={handleChange}
                />
                <label htmlFor="is_free_giveaway">Zu verschenken</label>
              </div>
            </>
          )}

          {/* Comment-specific fields */}
          {formData.content_type === 'comment' && (
            <div className="form-group">
              <label>Beitrag zum Kommentieren *</label>
              <select
                name="parent_post_id"
                value={formData.parent_post_id}
                onChange={handleChange}
                required
              >
                <option value="">Bitte wählen</option>
                {posts.map((post) => (
                  <option key={post.id} value={post.id}>
                    {post.content.substring(0, 50)}...
                  </option>
                ))}
              </select>
              {posts.length === 0 && formData.university && (
                <p className="hint">Keine Beiträge für diese Universität gefunden</p>
              )}
            </div>
          )}

          {/* Course review-specific fields */}
          {formData.content_type === 'course_review' && (
            <>
              <div className="form-group">
                <label>Kurs *</label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Bitte wählen</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name} ({course.code})
                    </option>
                  ))}
                </select>
                {courses.length === 0 && formData.university && (
                  <p className="hint">Keine Kurse für diese Universität gefunden</p>
                )}
              </div>

              <div className="form-group">
                <label>Bewertung *</label>
                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  required
                >
                  <option value={5}>5 Sterne</option>
                  <option value={4}>4 Sterne</option>
                  <option value={3}>3 Sterne</option>
                  <option value={2}>2 Sterne</option>
                  <option value={1}>1 Stern</option>
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Inhalt *</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows={5}
              placeholder={
                formData.content_type === 'course_review'
                  ? 'Bewertungstext eingeben...'
                  : 'Inhalt eingeben...'
              }
            />
          </div>


          {/* Image URLs for posts and marketplace */}
          {(formData.content_type === 'post' || formData.content_type === 'marketplace') && (
            <div className="form-group">
              <label>Bilder</label>
              <div className="image-urls-list">
                {formData.image_urls.map((url, index) => (
                  <div key={index} className="image-url-item">
                    <span>{url.substring(0, 50)}...</span>
                    <button
                      type="button"
                      className="btn btn-small btn-danger"
                      onClick={() => handleRemoveImageUrl(index)}
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleAddImageUrl}
              >
                Bild-URL hinzufügen
              </button>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Speichere...' : isEditing ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>

        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            border-radius: 8px;
            width: 90%;
            max-width: 600px;
            max-height: 90vh;
            overflow-y: auto;
            padding: 24px;
          }
          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .modal-header h2 {
            margin: 0;
          }
          .close-button {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
          }
          .form-row {
            display: flex;
            gap: 16px;
          }
          .form-row .form-group {
            flex: 1;
          }
          .form-group {
            margin-bottom: 16px;
          }
          .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
          }
          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
          }
          .form-group textarea {
            resize: vertical;
          }
          .checkbox-group {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .checkbox-group input {
            width: auto;
          }
          .checkbox-group label {
            margin-bottom: 0;
          }
          .hint {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
          }
          .image-urls-list {
            margin-bottom: 8px;
          }
          .image-url-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px;
            background: #f5f5f5;
            border-radius: 4px;
            margin-bottom: 4px;
          }
          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #eee;
          }
          .error-message {
            color: #d32f2f;
            background: #ffebee;
            padding: 12px;
            border-radius: 4px;
            margin-bottom: 16px;
          }
          .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          }
          .btn-primary {
            background: #1976d2;
            color: white;
          }
          .btn-primary:disabled {
            background: #90caf9;
            cursor: not-allowed;
          }
          .btn-secondary {
            background: #f5f5f5;
            color: #333;
          }
          .btn-danger {
            background: #d32f2f;
            color: white;
          }
          .btn-small {
            padding: 4px 8px;
            font-size: 12px;
          }
        `}</style>
      </div>
    </div>
  );
}

export default CreateTestDataModal;
